import { hasAnalyticsConsent } from "./cookieConsent";

/**
 * Options for the Meta Pixel's 4th `fbq('track', ...)` argument.
 */
export interface MetaPixelEventOptions {
  /**
   * Deduplication key. When the backend sends the same event name with the same id through
   * the Conversions API, Meta collapses the pair instead of counting two conversions.
   * See doc/meta-conversions-api.md.
   */
  eventID?: string;
}

export function trackMetaPixelEvent(
  eventName: string,
  params?: Record<string, unknown>,
  options?: MetaPixelEventOptions
): void {
  if (!hasAnalyticsConsent() || !window.fbq) return;
  // fbq treats a 4th argument as the event options object, so it is only passed when there
  // is actually something to put in it.
  if (options?.eventID) {
    window.fbq("track", eventName, params, options);
  } else {
    window.fbq("track", eventName, params);
  }
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"
    )
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * The browser ids fbevents.js writes as first-party cookies: `_fbp` identifies the browser,
 * `_fbc` records the ad click that brought it here. They are Meta's strongest match signals
 * and the backend cannot derive them, so they travel with the signup mutation to be attached
 * to the server-side event.
 *
 * Both are absent when the user declined the cookie banner, because the Pixel never loads —
 * which is the intended outcome, not a bug to work around.
 */
export function getMetaBrowserIds(): { fbp?: string; fbc?: string } {
  return { fbp: readCookie("_fbp"), fbc: readCookie("_fbc") };
}

/**
 * Shape of the `clientTracking` field on the backend's `createUser` mutation input.
 */
export interface ClientTrackingInput {
  analyticsConsent: boolean;
  fbp?: string;
  fbc?: string;
  eventSourceUrl: string;
}

/**
 * Collects what the backend needs to send the server-side half of a conversion. Call it
 * *before* the mutation it travels with. The consent flag is included explicitly so the
 * backend can honour the cookie banner too: a missing `_fbp` alone would be too weak a
 * signal to infer the user's choice from.
 */
export function buildClientTracking(): ClientTrackingInput {
  const { fbp, fbc } = getMetaBrowserIds();
  return {
    analyticsConsent: hasAnalyticsConsent(),
    fbp,
    fbc,
    eventSourceUrl: window.location.href,
  };
}

/**
 * The deduplication key for the signup Lead event. Both halves of the event derive it from
 * the user id `createUser` returns — the browser from the response, the backend from the user
 * it just created — so the two can never desync.
 *
 * Keep in sync with signupEventIDPrefix in
 * backend/features/marketing/domain_event_handlers/user_created_handler.go.
 */
export function signupLeadEventId(userId: string): string {
  return `signup_${userId}`;
}
